import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Stack, Typography } from "@mui/material";

import { Button, Form, FormTextField } from "@covertree/ui";
import { Modal } from "@covertree/ui";
import { InputLabelHeight } from "@covertree/types";
import { Toast } from "@covertree/utils";
import { useDiscardRenewalMutation } from "@covertree/agent-portal-graphql";

import { DiscardRenewalModalForm, discardRenewalReasonValidator } from "./form";
import { getErrorMessageFromApolloError } from "@utils/getErrorMessageFromApolloError";
import { delayRefetchedQuery } from "@utils/delayRefetchedQuery";

interface Props {
  renewalLocator?: string;
  shouldRefetchListAfterDiscard: boolean;
  closeModal(): void;
}

export function DiscardRenewalModal({
  closeModal,
  renewalLocator,
  shouldRefetchListAfterDiscard,
}: Props) {
  const [
    discardRenewalMutation,
    { loading, reset: resetDiscardRenewalMutation },
  ] = useDiscardRenewalMutation({
    refetchQueries: shouldRefetchListAfterDiscard
      ? ["GetRenewalListItems"]
      : undefined,
    onQueryUpdated: shouldRefetchListAfterDiscard
      ? delayRefetchedQuery
      : undefined,
  });

  const form = useForm({
    mode: "all",
    resolver: yupResolver(discardRenewalReasonValidator),
  });

  const onClose = useCallback(() => {
    if (!loading) {
      closeModal();
      form.reset();
      resetDiscardRenewalMutation();
    }
  }, [closeModal, form.reset, loading, resetDiscardRenewalMutation]);

  const handleSubmit = useCallback(
    async ({ reason }: DiscardRenewalModalForm) => {
      if (loading) {
        return;
      }

      try {
        const { data } = await discardRenewalMutation({
          variables: { renewalLocator, reason },
        });

        if (data?.discardRenewal) {
          onClose();
          Toast.showSuccess("Renewal was successfully discarded!");
        }
      } catch (error) {
        Toast.showError(
          getErrorMessageFromApolloError(error) ??
            "Could not discard renewal. Please try again"
        );
      }
    },
    [discardRenewalMutation, renewalLocator, loading]
  );

  return (
    <Modal
      open={!!renewalLocator}
      onClose={onClose}
      title="Are you sure?"
      disableClose={loading}
      maxWidth="sm">
      <Form onSubmit={form.handleSubmit(handleSubmit)}>
        <Typography gutterBottom>
          Do you want to discard renewal{" "}
          <Typography component="span" color="secondary">
            {renewalLocator}
          </Typography>
          ?
        </Typography>
        <FormProvider {...form}>
          <FormTextField
            multiline
            minRows={4}
            maxRows={4}
            control={form.control}
            name="reason"
            id="discard-renewal-reason-description"
            label=""
            placeholder="Please provide a reason..."
            labelHeight={InputLabelHeight.SMALL}
            hiddenLabel
            disabled={loading}
            InputProps={{
              sx: (theme) => ({
                "& > textarea": {
                  cursor: "auto",
                  paddingX: 0,
                  paddingTop: 1,
                },
                "& > textarea::-webkit-scrollbar": {
                  width: theme.spacing(1),
                },
                "& > textarea::-webkit-scrollbar-thumb": {
                  borderRadius: theme.spacing(1),
                  backgroundColor: theme.colors.scrollbar,
                },
              }),
            }}
          />
          <Stack direction="row" spacing={3} mt={4}>
            <Button
              loading={loading}
              disabled={loading || !form.formState.isValid}
              customColorVariant="error"
              type="submit">
              Discard
            </Button>
            <Button disabled={loading} onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </FormProvider>
      </Form>
    </Modal>
  );
}
